/**
 * @file HomePage.tsx
 * @brief The user's Home on the web and desktop app, the phone's Home.
 *
 * @details
 * DES, 30-09-2026: "web and desktop users will have the same exact experience
 * as mobile". This is `FRONTEND/src/screens/Home/HomeScreen.tsx` for a user
 * (`/app`), a company admin (`/company/home`, 30-09-2026) and a super admin
 * (`/admin/home`, 13-10-2026), who on the phone also open on Home:
 * - "Welcome back, {First}", or "Welcome, {First}" for a new user who has not
 *   been greeted on this browser.
 * - Quick action pills.
 * - My to-dos (12-10-2026): overdue / today / tomorrow from every meeting,
 *   ticked in place, with "See all (N)" to `/todos`. Hidden while it first
 *   loads and when nothing is open, as on the phone.
 * - Quick actions and the rails (Scheduled Sessions, Recent Boardroom
 *   Meetings, Recent Interpretation Sessions, Recent Quick Notes) in the
 *   order the user dragged Home into on the phone, else the phone's default.
 * - The whole-page empty state.
 * - The slide dock: Boardroom, Interpret or a Note, by the user's main use
 *   (slideTarget.ts, DES 12-10-2026).
 * - Each rail hides until it has items, and is gated by the same entitlement
 *   as on the phone.
 * - Collapsed sections are the phone's own preference
 *   (`homeCollapsedSections` / `PUT /auth/home-preferences`), so collapsing
 *   one on the laptop collapses it on the phone.
 *
 * - The Ask pill (the phone's red-tint 68px circle) opens the web's
 *   AskMeetingsPanel in a sheet; with no meetings yet it says so, as the
 *   phone's AskNoMeetingsSheet does. Since 04-10-2026 it plays the phone's
 *   shrink-from-wide intro too (AskIntroPill.tsx).
 *
 * **Not on the web yet (deliberately, for now):**
 * - Starting or joining an interpretation session (phone only). Since
 *   01-10-2026 a user's sessions are listed at /app/sessions and open as a
 *   page; the rail's cards and the Interpretation pill go there.
 * - Drag-to-reorder: none on purpose (DES 11-10-2026). The sections follow
 *   the order dragged on the phone (`homeSectionOrder`, homeSectionOrder.ts).
 *
 * **Calm Home (DES 19-10-2026, CALM_HOME):**
 * - "Coming up" on top: the next calendar meetings with "Start notes", else
 *   one "Connect your calendar" card (ComingUpSection.tsx).
 * - "Try a demo meeting" while the user has no meetings (homeFirstRun.tsx);
 *   afterwards the footer's "Demo meeting" link opens it.
 * - An empty meetings rail shows blurred example meetings behind "Your
 *   meetings will appear here." instead of the whole-page empty state.
 * - One red + in the bottom-right corner where the slide was (DES 20-10-2026,
 *   option D; was two dock buttons, New meeting and Interpret): a small menu
 *   with Meeting notes and Boardroom, both through Setup. CALM_HOME = false
 *   brings the slide back.
 * - The quick-action pills (Ask, Boardroom, Notes, Interpretation,
 *   Assessments) stay, as before Calm Home (DES 20-10-2026: Calm Home hid
 *   them 19-10, which was a mistake in the brief).
 *
 * @author DES
 * @date 30-09-2026
 */

import { EmptyState } from "@/components/feedback/EmptyState";
import { Fragment, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { backStateHere } from "@/shared/navigation/backState";
import { useBoardroomBasePath } from "@/features/employee-boardroom/useBoardroomBasePath";
import { ArrowRight, CalendarClock, Languages, PenLine, Plus, type LucideIcon } from "lucide-react";
import { BoardroomTable } from "@/components/icons/BoardroomTable";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/DropdownMenu";
import { MedicalReport } from "@/components/icons/MedicalReport";
import { SwipeButton } from "@/components/ui/SwipeButton";
import { BrandMarkSlideIcon } from "@/components/brand/BrandMarkGlyph";
import { AskIntroPill } from "./AskIntroPill";
import { AskMeetingsPanel } from "@/features/company-meetings/components/AskMeetingsPanel";
import { AskPalette } from "@/features/company-meetings/components/AskPalette";
import { useCompanyFeatures } from "@/features/company-features/api";
import { useAuth } from "@/shared/auth/auth-context";
import { useLoadingSkeleton } from "@/shared/hooks/useLoadingSkeleton";
import { MainScrollContext } from "@/shared/scroll/main-scroll-context";
import { cn } from "@/shared/theme/cn";
import { dockFade } from "@/shared/theme/dockGlass";
import {
  saveHomeCollapsed,
  useHomeMeetings,
  useHomeNotes,
  useHomeScheduled,
  useHomeSessions,
  useHomeUsage,
} from "./homeApi";
import { slideTargetFor } from "./slideTarget";
import { HomeRail, HomeSection, NoteRailSkeleton, RailSkeleton } from "./homeRail";
import { resolveWebHomeOrder, type WebHomeSectionKey } from "./homeSectionOrder";
import {
  CARD_W,
  MEETING_BODY_H,
  MeetingCard,
  NoteTimelineItem,
  SCHEDULED_W,
  ScheduledCard,
  SESSION_BODY_H,
  SessionCard,
} from "./homeCards";
import { env } from "@/shared/config/env";
import { companyIdOf, webInterpretAllowed } from "@/features/interpret/pilot";
import { TodoTick } from "@/components/icons/TodoTick";
import { useMyTodos } from "@/features/todos/myTodosApi";
import { MyTodosList } from "@/features/todos/MyTodosList";
import { useDesktopCalendar } from "@/features/employee-boardroom/useDesktopCalendar";
import { ComingUpSection } from "./ComingUpSection";
import { DemoMeetingCard, EmptyMeetingsPreview } from "./homeFirstRun";
import { DataControlsNotice } from "@/features/company-data-controls/DataControlsSection";
import { isCompanyAdminRole } from "@/shared/auth/homePath";
import { MineTeamSwitch } from "@/features/company-meetings/components/MineTeamSwitch";
import { TEAM_EMPTY_TITLE, useMeetingListScope } from "@/features/company-meetings/meetingListScope";
import { PlanPillWithUpgrade } from "@/shared/billing/PlanPill";
import { usePlanSummary } from "@/shared/billing/usePlanSummary";
import { FreeMinutesNotice } from "@/features/my-billing/components/FreeMinutesNotice";

/** Calm Home (DES 19-10-2026): Coming up, the demo card, the blurred empty
 *  meetings and two primary actions. false = the slide layout. The
 *  quick-action pills show either way (20-10-2026). */
const CALM_HOME = true;

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

type HomeUserExtras = {
  firstName?: string | null;
  name?: string | null;
  createdAt?: string | null;
  created_at?: string | null;
  homeCollapsedSections?: string[] | null;
  /** The order dragged on the phone (11-10-2026); null = never dragged. */
  homeSectionOrder?: string[] | null;
  /** Main use from signup / Profile: sets the slide button (12-10-2026). */
  primaryUse?: string | null;
};

/** The phone's `useWelcomeGreeting`, decided once per page load. */
function useGreeting(user: (HomeUserExtras & { id?: string }) | null): string {
  const [greeting] = useState(() => {
    const first = (user?.firstName || user?.name || "").trim().split(/\s+/)[0] || "";
    const created = Date.parse(user?.createdAt ?? user?.created_at ?? "");
    const key = `verblizr.greeted.${user?.id ?? ""}`;
    let greeted = true;
    try {
      greeted = localStorage.getItem(key) === "1";
      localStorage.setItem(key, "1");
    } catch {
      /* storage blocked: treat as greeted */
    }
    const isNew = Number.isFinite(created) && Date.now() - created < WEEK_MS && !greeted;
    const head = isNew ? "Welcome" : "Welcome back";
    return first ? `${head}, ${first}` : head;
  });
  return greeting;
}

/**
 * A Quick Actions pill: 68 tall, fully round, 36px brand icon. Half the
 * screen less the gutters on a phone-width window (so the next one peeks in,
 * as on the phone), capped at 220px on a desktop. A link, or a button when
 * the action opens a sheet.
 */
const pillClass =
  "flex h-[68px] w-[min(220px,calc((100vw-52px)/2))] shrink-0 items-center gap-2.5 rounded-full bg-black/[0.04] pl-4 pr-3.5 text-left transition-colors hover:bg-black/[0.08] dark:bg-white/[0.06] dark:hover:bg-white/[0.12]";

function QuickActionPill({
  icon: Icon,
  title,
  subtitle,
  to,
  onClick,
}: {
  icon: LucideIcon;
  title: string;
  subtitle: string;
  to?: string;
  onClick?: () => void;
}) {
  const body = (
    <>
      <Icon className="h-9 w-9 shrink-0 text-brand" strokeWidth={1.5} />
      <span className="min-w-0">
        <span className="block truncate text-[15px] font-semibold text-app-text">{title}</span>
        <span className="mt-px block truncate text-xs font-medium text-app-text-secondary">
          {subtitle}
        </span>
      </span>
    </>
  );
  return to ? (
    <Link to={to} className={pillClass}>
      {body}
    </Link>
  ) : (
    <button type="button" onClick={onClick} className={pillClass}>
      {body}
    </button>
  );
}

/**
 * Calm Home's start button (DES 20-10-2026, option D): a red + in the
 * bottom-right corner. Click: a small menu above it with Meeting notes and
 * Boardroom, each with one line on what it is; the + turns into an ×.
 */
function StartMenu({ onNotes, onBoardroom }: { onNotes: () => void; onBoardroom: () => void }) {
  const [open, setOpen] = useState(false);
  const item =
    "flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 outline-none data-[highlighted]:bg-black/[0.05] dark:data-[highlighted]:bg-white/[0.07]";
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={open ? "Close" : "Start a meeting"}
          className="pointer-events-auto inline-flex size-14 items-center justify-center rounded-full bg-brand text-white shadow-[0_8px_24px_rgba(229,36,33,0.35)] transition-colors hover:bg-brand/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:ring-offset-2"
        >
          <Plus
            className={cn("size-7 transition-transform duration-200", open && "rotate-45")}
            strokeWidth={2.2}
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="end" sideOffset={12} className="w-64 rounded-2xl p-1.5">
        <DropdownMenuItem className={item} onSelect={onNotes}>
          <PenLine className="size-5 shrink-0 text-brand" strokeWidth={2} />
          <span className="min-w-0">
            <span className="block text-[15px] font-semibold text-app-text">Meeting notes</span>
            <span className="block text-xs text-app-text-secondary">Quick capture, private to you</span>
          </span>
        </DropdownMenuItem>
        <DropdownMenuItem className={item} onSelect={onBoardroom}>
          <BoardroomTable className="size-5 shrink-0 text-brand" />
          <span className="min-w-0">
            <span className="block text-[15px] font-semibold text-app-text">Boardroom</span>
            <span className="block text-xs text-app-text-secondary">Speakers, decisions, minutes</span>
          </span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function HomePage() {
  // Mounted for a user (/app), a company admin (/company/home) and a super
  // admin (/admin/home, 13-10-2026); links stay inside the shell the page is in.
  const BASE = useBoardroomBasePath();
  // A meeting opened from Home comes back to Home (DES 13-10-2026).
  const backHome = backStateHere(useLocation(), "Home");
  const { user, entitlements } = useAuth();
  const navigate = useNavigate();
  const extras = (user ?? null) as (HomeUserExtras & { id?: string }) | null;
  const greeting = useGreeting(extras);

  // Outside the shell (tests, previews) there is no scroll context: no-op.
  const scroll = useContext(MainScrollContext);
  const chromeCollapsed = scroll?.chromeCollapsed ?? false;
  const setFlushBottom = scroll?.setFlushBottom;
  useEffect(() => {
    setFlushBottom?.(true);
    return () => setFlushBottom?.(false);
  }, [setFlushBottom]);

  const canMeet = entitlements.boardroom !== false;
  const canInterpret = entitlements.interpretation !== false;
  const canNote = entitlements.notes !== false;
  // Live interpretation on the web is a pilot (01-10-2026).
  const webInterpret =
    canInterpret && webInterpretAllowed(companyIdOf(user), env.webInterpretCompanies);

  // The slide follows the main use, as on the phone (slideTarget.ts).
  const usage = useHomeUsage(canMeet && webInterpret);
  const slide = slideTargetFor(
    extras?.primaryUse,
    { boardroom: canMeet, interpretation: webInterpret, notes: canNote },
    usage.data,
  );

  const scheduled = useHomeScheduled(user?.id, canInterpret);
  // Mine | Team for a company admin (22-10-2026), the same choice as the lists.
  const listScope = useMeetingListScope();
  const teamScope = listScope.scope === "team";
  const meetings = useHomeMeetings(canMeet, listScope.scope);
  const sessions = useHomeSessions(user?.id, canInterpret);
  // The session card's `#` / pen editors (04-10-2026).
  const notes = useHomeNotes(canNote);
  // My to-dos: the to-dos live in Boardroom meetings (12-10-2026).
  const todos = useMyTodos({ view: "home" }, canMeet);
  // Coming up (calm Home): the Mac's calendar in the desktop app.
  const calendar = useDesktopCalendar({ userId: user?.id, selfEmail: user?.email });
  const scheduledLoading = useLoadingSkeleton(
    "home.scheduled",
    scheduled.isLoading,
    scheduled.data?.length,
  );
  const meetingsLoading = useLoadingSkeleton(
    "home.meetings",
    meetings.isLoading,
    meetings.data?.length,
  );
  const sessionsLoading = useLoadingSkeleton(
    "home.sessions",
    sessions.isLoading,
    sessions.data?.length,
  );
  const notesLoading = useLoadingSkeleton("home.notes", notes.isLoading, notes.data?.length);
  const [askOpen, setAskOpen] = useState(false);
  const { plan } = usePlanSummary();
  // An admin's empty Team rail is not a first run.
  const noMeetingsYet =
    canMeet && !teamScope && meetings.isFetched && (meetings.data?.length ?? 0) === 0;
  // Assessments: only where the web has the page (the company shell) and the
  // product is not locked platform-wide, the same rule as its route guard.
  const inCompany = BASE === "/company";
  const companyFeatures = useCompanyFeatures(inCompany);
  const canAssess =
    inCompany &&
    Boolean(companyFeatures.data) &&
    companyFeatures.data?.platformLocks?.medical_assessment !== true;

  // Collapsed sections: the phone's preference, shared through the server.
  const [collapsed, setCollapsed] = useState<Set<string>>(
    () => new Set(extras?.homeCollapsedSections ?? []),
  );
  useEffect(() => {
    if (extras?.homeCollapsedSections) setCollapsed(new Set(extras.homeCollapsedSections));
  }, [extras?.homeCollapsedSections]);
  const toggle = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      // Best effort, like the phone: a failed save only costs the sync.
      void saveHomeCollapsed([...next]).catch(() => undefined);
      return next;
    });

  const rails = [
    { on: canInterpret, q: scheduled },
    { on: canMeet, q: meetings },
    { on: canInterpret, q: sessions },
    { on: canNote, q: notes },
  ].filter((r) => r.on);
  const homeReady = rails.every((r) => r.q.isFetched);
  const allEmpty = useMemo(
    // An admin on Team sees the rail's own "No team meetings yet." instead.
    () =>
      !teamScope &&
      rails.length > 0 &&
      rails.every((r) => r.q.isFetched && (r.q.data?.length ?? 0) === 0),
    [rails, teamScope],
  );

  // The phone's dragged order (DES 11-10-2026); the default until it has one.
  const sectionOrder = useMemo(
    () => resolveWebHomeOrder(extras?.homeSectionOrder),
    [extras?.homeSectionOrder],
  );
  const sectionEls = {} as Record<WebHomeSectionKey, ReactNode>;
  const pillsLead = sectionOrder[0] === "quickActions";
  // Quick actions stay on Calm Home too (DES 20-10-2026: hiding them was a
  // mistake in the 19-10 brief); exactly as before, same role rules.
  sectionEls.quickActions = (
    <div className="mb-4 mt-5 flex gap-3 overflow-x-auto px-5 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {canMeet ? (
        // The phone's intro (04-10-2026): wide with its label, then the
        // circle once Home's rails have answered.
        <AskIntroPill ready={homeReady} onClick={() => setAskOpen(true)} />
      ) : null}
      {canMeet ? (
        <QuickActionPill
          icon={BoardroomTable} // DES 08-10-2026: app icon, was Presentation
          title="Boardroom"
          subtitle="Meetings"
          to={`${BASE}/meetings`}
        />
      ) : null}
      {canNote ? (
        <QuickActionPill
          icon={PenLine} // DES 08-10-2026: app icon, was NotebookPen
          title="Notes"
          subtitle="Quick capture"
          to={`${BASE}/notes`}
        />
      ) : null}
      {/* The phone's order: the slide's product (Boardroom), Notes, then
          the rest. Interpretation opens the interpreter where the web pilot
          is on (01-10-2026), otherwise its Sessions page. */}
      {canInterpret ? (
        <QuickActionPill
          icon={Languages}
          title="Interpretation"
          subtitle="Turn-by-turn"
          to={webInterpret ? `${BASE}/interpret` : `${BASE}/sessions`}
        />
      ) : null}
      {canAssess ? (
        <QuickActionPill
          icon={MedicalReport} // DES 08-10-2026: app icon, was FileHeart
          title="Assessments"
          subtitle="Medico-legal"
          to={`${BASE}/medical-assessment`}
        />
      ) : null}
    </div>
  );
  // Hidden while it first loads and when nothing is open (DES 12-10-2026).
  const openTodos = todos.data?.counts.open ?? 0;
  sectionEls.todos =
    canMeet && todos.data && openTodos > 0 && todos.data.groups.length > 0 ? (
      <HomeSection
        icon={TodoTick}
        eyebrow={null}
        title="My to-dos"
        toggleLabel="My to-dos"
        collapsed={collapsed.has("todos")}
        onToggle={() => toggle("todos")}
      >
        <MyTodosList
          data={todos.data}
          meetingHref={(id) => `${BASE}/meetings/${id}`}
          backLabel="Home"
          // Home: by meeting, as the phone (DES 14-10-2026).
          grouping="meeting"
          // No card: rows straight on the page, as the phone (DES 13-10-2026).
          className="px-4"
        />
        {/* "See all (N) →" under the list, as Quick Notes' "See all notes"
            (DES 14-10-2026, phone and web), lined up with the rows' text (list px-4 + row px-1 + 22 circle + gap-3 = 54); the heading keeps only the
            collapse chevron like every other section. */}
        <Link
          to={`${BASE}/todos`}
          className="ml-[54px] mt-1 inline-flex items-center gap-1.5 text-[15px] font-semibold text-brand hover:underline"
        >
          See all ({openTodos})
          <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
        </Link>
      </HomeSection>
    ) : null;
  sectionEls.scheduled =
    canInterpret && ((scheduled.data?.length ?? 0) > 0 || scheduledLoading) ? (
      <HomeSection
        icon={CalendarClock}
        eyebrow={null}
        title={
          scheduled.data?.length ? (
            <>
              Scheduled Sessions - <span className="text-brand">{scheduled.data.length}</span>
            </>
          ) : (
            "Scheduled Sessions"
          )
        }
        toggleLabel="Scheduled Sessions"
        collapsed={collapsed.has("scheduled")}
        onToggle={() => toggle("scheduled")}
      >
        {scheduled.data?.length ? (
          <HomeRail cardWidth={SCHEDULED_W} bodyHeight={96} seeAllTo={`${BASE}/schedule`}>
            {scheduled.data.map((row) => (
              <ScheduledCard
                key={row.id}
                row={row}
                // The phone's card is "Join": the interpreter, prefilled.
                to={
                  webInterpret
                    ? `${BASE}/interpret?booking=${encodeURIComponent(row.id)}`
                    : `${BASE}/schedule`
                }
              />
            ))}
          </HomeRail>
        ) : (
          <RailSkeleton cardWidth={SCHEDULED_W} bodyHeight={96} />
        )}
      </HomeSection>
    ) : null;
  sectionEls.meetings =
    CALM_HOME && noMeetingsYet ? (
      // Calm Home: what the rail will look like, blurred (DES 19-10-2026).
      <HomeSection
        icon={BoardroomTable}
        eyebrow={null}
        title="Boardroom Meetings"
        toggleLabel="Meetings"
        collapsed={collapsed.has("meetings")}
        onToggle={() => toggle("meetings")}
        action={<MineTeamSwitch />}
      >
        <EmptyMeetingsPreview />
      </HomeSection>
    ) : canMeet && ((meetings.data?.length ?? 0) > 0 || meetingsLoading || teamScope) ? (
      <HomeSection
        icon={BoardroomTable} // DES 08-10-2026: app icon, was Presentation
        title="Boardroom Meetings"
        toggleLabel="Meetings"
        collapsed={collapsed.has("meetings")}
        onToggle={() => toggle("meetings")}
        action={<MineTeamSwitch />}
      >
        {teamScope && meetings.isFetched && !meetings.data?.length ? (
          <p className="px-5 py-6 text-[15px] text-app-text-secondary">{TEAM_EMPTY_TITLE}</p>
        ) : meetings.data?.length ? (
          <HomeRail
            cardWidth={CARD_W}
            bodyHeight={MEETING_BODY_H}
            seeAllTo={`${BASE}/meetings`}
            headInside
          >
            {meetings.data.map((m) => (
              <MeetingCard
                key={m.id}
                meeting={m}
                to={`${BASE}/meetings/${m.id}`}
                state={backHome}
                showOwner={teamScope}
              />
            ))}
          </HomeRail>
        ) : (
          <RailSkeleton cardWidth={CARD_W} bodyHeight={MEETING_BODY_H} headInside />
        )}
      </HomeSection>
    ) : null;
  sectionEls.sessions =
    canInterpret && ((sessions.data?.length ?? 0) > 0 || sessionsLoading) ? (
      <HomeSection
        icon={Languages}
        title="Interpretation Sessions"
        toggleLabel="Sessions"
        collapsed={collapsed.has("sessions")}
        onToggle={() => toggle("sessions")}
      >
        {/* "See All" like the other rails (DES 01-10-2026): sessions
            have a list page for users and admins now. */}
        {sessions.data?.length ? (
          <HomeRail
            cardWidth={CARD_W}
            bodyHeight={SESSION_BODY_H}
            seeAllTo={`${BASE}/sessions`}
            headInside
          >
            {sessions.data.map((s) => (
              <SessionCard
                key={s.session_id}
                session={s}
                to={`${BASE}/sessions/${encodeURIComponent(s.session_id)}`}
                currentUserEmail={user?.email}
              />
            ))}
          </HomeRail>
        ) : (
          <RailSkeleton cardWidth={CARD_W} bodyHeight={SESSION_BODY_H} headInside />
        )}
      </HomeSection>
    ) : null;
  sectionEls.notes =
    canNote && ((notes.data?.length ?? 0) > 0 || notesLoading) ? (
      <HomeSection
        icon={PenLine} // DES 08-10-2026: app icon, was NotebookPen
        title="Quick Notes"
        toggleLabel="Notes"
        rule={false}
        collapsed={collapsed.has("notes")}
        onToggle={() => toggle("notes")}
      >
        {/* The Notes list's rows on their side (DES 07-10-2026): the
            phone lists them; the desktop keeps a swipe rail. */}
        {notes.data?.length ? (
          <HomeRail cardWidth={CARD_W} bodyHeight={120} seeAllTo={`${BASE}/notes`} seeAllTop={40}>
            {notes.data.map((n, i, all) => (
              <NoteTimelineItem
                key={n.id}
                note={n}
                to={`${BASE}/notes/${n.id}`}
                first={i === 0}
                last={i === all.length - 1}
              />
            ))}
          </HomeRail>
        ) : (
          <NoteRailSkeleton cardWidth={CARD_W} />
        )}
      </HomeSection>
    ) : null;

  return (
    <div className="flex min-h-[calc(100dvh-4rem)] flex-col">
      {/* Full width (DES 30-09-2026, as the meeting detail): sections keep
          their own 20px sides, plus 12px from md = the detail's 32px. */}
      <div className="w-full flex-1 pt-6 md:px-3">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-5">
          <h1 className="text-[22px] font-bold text-app-text">{greeting}</h1>
          {/* Which plan, and a quiet Upgrade on Free (21-10-2026). */}
          {plan ? <PlanPillWithUpgrade plan={plan} /> : null}
        </div>
        {/* Free plan minutes (B4, 22-10-2026); nothing when not on Free. */}
        {canMeet ? <FreeMinutesNotice className="mx-5 mt-3" /> : null}
        {/* ADR-051 (21-10-2026): the one-time "inside the company" notice. */}
        {isCompanyAdminRole(user?.role) ? (
          <DataControlsNotice linkToSettings className="mx-5 mt-4" />
        ) : null}

        {/* The pills keep their place under the greeting when they lead the
            phone's order, above Coming up (DES 20-10-2026). */}
        {pillsLead ? sectionEls.quickActions : null}

        {CALM_HOME && user?.id ? (
          <ComingUpSection
            calendar={calendar}
            userId={String(user.id)}
            boardroomBase={BASE}
            canRecord={canMeet}
          />
        ) : null}
        {/* Until the first meeting; then Help's "Demo meeting" (footer). */}
        {CALM_HOME && noMeetingsYet ? (
          <DemoMeetingCard to={`${BASE}/meetings/demo`} state={backHome} />
        ) : null}

        {/* ---------------- Sections, in the phone's order ---------------- */}
        {/* DES 11-10-2026: the order the user dragged Home into on the phone
            (homeSectionOrder.ts); no drag here. */}
        {sectionOrder
          .filter((key) => !(pillsLead && key === "quickActions"))
          .map((key) => (
            <Fragment key={key}>{sectionEls[key]}</Fragment>
          ))}

        {/* ---------------- Empty ---------------- */}
        {allEmpty && !(CALM_HOME && canMeet) ? (
          <EmptyState
            shape="row"
            title="Nothing here yet"
            description="Your meetings, sessions and notes will show up here once you start."
          />
        ) : null}
      </div>

      {/* ---------------- Ask ---------------- */}
      {/* The Cmd+K palette's look (DES 01-10-2026). */}
      <AskPalette open={askOpen} onOpenChange={setAskOpen}>
        {meetings.isFetched && (meetings.data?.length ?? 0) === 0 ? (
          <div className="px-6 py-10 text-center">
            <p className="text-lg font-bold text-app-text">No meetings to ask yet</p>
            <p className="mt-2 text-[15px] text-app-text-secondary">
              Record a boardroom meeting, then ask anything about what was said.
            </p>
          </div>
        ) : (
          <AskMeetingsPanel meetingHref={(id) => `${BASE}/meetings/${id}`} />
        )}
      </AskPalette>

      {/* ---------------- Dock ---------------- */}
      {CALM_HOME && canMeet ? (
        // One red + in the corner (DES 20-10-2026, option D): no dock; it
        // opens a small menu with Meeting notes and Boardroom. Both go to
        // Setup (its recording notice first); Notes opens it in Notes mode.
        // Interpretation stays in the quick-action pills.
        <div
          className={cn(
            "pointer-events-none sticky bottom-0 z-20 mt-auto flex justify-end px-5 pb-5 pt-6 transition-transform duration-300 ease-out motion-reduce:transition-none",
            chromeCollapsed && "translate-y-[calc(100%+4rem)]",
          )}
        >
          <StartMenu
            onNotes={() => navigate(`${BASE}/meetings/new`, { state: { tier: "notes" } })}
            onBoardroom={() => navigate(`${BASE}/meetings/new`)}
          />
        </div>
      ) : slide ? (
        <div
          // The shell drops its 16px bottom gap for Home (setFlushBottom), so
          // the fade meets the footer's blur. When the shell's footer slides
          // away on scroll-down, the dock goes with it, as the phone's dock
          // does; nothing is left floating over an empty band. (30-09-2026)
          className={cn(
            // THE dock fade (shared/theme/dockGlass.ts), never solid; was a
            // fade to solid page colour (DES 08-10-2026).
            "sticky bottom-0 z-20 mt-auto pb-3 pt-9 transition-transform duration-300 ease-out motion-reduce:transition-none",
            dockFade,
            chromeCollapsed && "pointer-events-none translate-y-[calc(100%+4rem)]",
          )}
        >
          <div className={cn("mx-auto w-full max-w-md px-5")}>
            {slide === "boardroom" ? (
              <SwipeButton
                text="Slide for Boardroom"
                icon={BrandMarkSlideIcon} // DES 08-10-2026: app icon, was Users (the phone's mark on both slides)
                onSwipeComplete={() => navigate(`${BASE}/meetings/new`)}
                accessibilityLabel="Start a boardroom meeting"
                accessibilityHint="Slide or press Enter to set up a new meeting"
              />
            ) : slide === "note" ? (
              <SwipeButton
                text="Slide for a Note"
                icon={BrandMarkSlideIcon}
                onSwipeComplete={() => navigate(`${BASE}/notes/new`)}
                accessibilityLabel="Write a note"
                accessibilityHint="Slide or press Enter to start a new note"
              />
            ) : (
              <SwipeButton
                text="Slide to Interpret"
                icon={BrandMarkSlideIcon} // DES 08-10-2026: app icon, was Languages
                onSwipeComplete={() => navigate(`${BASE}/interpret`)}
                accessibilityLabel="Start interpretation"
                accessibilityHint="Slide or press Enter to set up an interpretation session"
              />
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default HomePage;
